import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  FileEdit,
  CalendarCheck,
  Image,
  ClipboardCheck,
  ArrowRight,
} from 'lucide-react';

import ReporterStatCard from '../../components/reporter/ReporterStatCard';
import ReporterArticleTable from '../../components/reporter/ReporterArticleTable';
import AssignedEventCard from '../../components/reporter/AssignedEventCard';
import MediaPreviewCard from '../../components/reporter/MediaPreviewCard';
import AdminRemarksCard from '../../components/reporter/AdminRemarksCard';

import { fetchMyNews } from '../../utils/api/newsApi';
import { fetchMyMedia } from '../../utils/api/mediaApi';
import api from '../../utils/api';

const quickActions = [
  {
    icon: FileEdit,
    labelEn: 'Create New Article',
    labelNe: 'नयाँ लेख लेख्नुहोस्',
    path: '/reporter/create-article',
    color: 'bg-primary text-white hover:bg-primary-500',
  },
  {
    icon: CalendarCheck,
    labelEn: 'Submit Event Report',
    labelNe: 'कार्यक्रम रिपोर्ट बुझाउनुहोस्',
    path: '/reporter/events',
    color: 'bg-amber-50 text-amber border border-amber-200 hover:bg-amber-100',
  },
  {
    icon: Image,
    labelEn: 'Upload Media',
    labelNe: 'मिडिया अपलोड गर्नुहोस्',
    path: '/reporter/media',
    color: 'bg-primary-50 text-primary border border-primary-200 hover:bg-primary-100',
  },
  {
    icon: ClipboardCheck,
    labelEn: 'View Review Status',
    labelNe: 'समीक्षा स्थिति हेर्नुहोस्',
    path: '/reporter/articles',
    color: 'bg-char-50 text-char-600 border border-char-200 hover:bg-char-100',
  },
];

const content = {
  ne: {
    welcome: 'स्वागत छ, रिपोर्टर',
    instruction: 'आजको समाचार, कार्यक्रम रिपोर्ट र मिडिया सामग्री यहाँबाट व्यवस्थापन गर्नुहोस्।',
    stats: {
      total: 'कुल लेखहरू',
      pending: 'समीक्षामा रहेका',
      published: 'प्रकाशित लेखहरू',
      rejected: 'अस्वीकृत लेखहरू',
    },
    subtitleTotal: 'सबै लेखहरू',
    subtitlePending: 'स्वीकृतिको पर्खाइमा',
    subtitlePublished: 'साइटमा लाइभ',
    subtitleRejected: 'संशोधन आवश्यक',
    quickActionsTitle: 'द्रुत कार्यहरू',
    createArticle: 'नयाँ लेख लेख्नुहोस्',
    submitEvent: 'कार्यक्रम रिपोर्ट बुझाउनुहोस्',
    uploadMedia: 'मिडिया अपलोड गर्नुहोस्',
    viewReview: 'समीक्षा स्थिति हेर्नुहोस्',
    adminRemarks: 'प्रशासक टिप्पणीहरू',
    viewAll: 'सबै हेर्नुहोस्',
    recentArticles: 'हालैका लेखहरू',
    assignedEvents: 'तोकिएका कार्यक्रमहरू',
    noEvents: 'कुनै कार्यक्रम छैन',
    recentMedia: 'हालैका मिडिया',
  },
  en: {
    welcome: 'Welcome, Reporter',
    instruction: 'Manage today\'s news, event reports, and media submissions from here.',
    stats: {
      total: 'Total Articles',
      pending: 'Pending Review',
      published: 'Published Articles',
      rejected: 'Rejected Articles',
    },
    subtitleTotal: 'All time articles',
    subtitlePending: 'Awaiting approval',
    subtitlePublished: 'Live on site',
    subtitleRejected: 'Needs revision',
    quickActionsTitle: 'Quick Actions',
    createArticle: 'Create New Article',
    submitEvent: 'Submit Event Report',
    uploadMedia: 'Upload Media',
    viewReview: 'View Review Status',
    adminRemarks: 'Admin Remarks',
    viewAll: 'View All',
    recentArticles: 'Recent Articles',
    assignedEvents: 'Assigned Events',
    noEvents: 'No events assigned',
    recentMedia: 'Recent Media',
  },
};

const ReporterDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = content[language] || content.en;
  const [stats, setStats] = useState({ totalArticles: 0, pendingReview: 0, publishedArticles: 0, rejectedArticles: 0 });
  const [recentArticles, setRecentArticles] = useState([]);
  const [rejectedArticles, setRejectedArticles] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentMedia, setRecentMedia] = useState([]);

  useEffect(() => {
    fetchMyNews()
      .then(res => {
        const articles = res.data || [];
        const counts = res.counts || {};
        setStats({
          totalArticles: counts.total || articles.length,
          pendingReview: counts.pending || 0,
          publishedArticles: counts.published || 0,
          rejectedArticles: counts.rejected || 0,
        });
        setRecentArticles(articles.slice(0, 4));
        setRejectedArticles(articles.filter(a => a.status === 'rejected'));
      })
      .catch(() => {});

    api.get('/events')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setUpcomingEvents(data.filter(e => e.status === 'upcoming' || e.status === 'current').slice(0, 3));
      })
      .catch(() => {});

    fetchMyMedia()
      .then(res => {
        const data = res.data || [];
        setRecentMedia(data.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div className="rounded-3xl bg-gradient-to-br from-primary-500 to-primary-600 p-6 text-white shadow-green sm:p-8">
        <h2 className="text-2xl font-extrabold sm:text-3xl">
          {t.welcome}
          {user?.name && (
            <span className="block text-lg font-bold text-white/80 sm:inline sm:text-2xl">
              , {user.name}
            </span>
          )}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/80">
          {t.instruction}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReporterStatCard
          icon={FileText}
          label={t.stats.total}
          value={stats.totalArticles}
          color="green"
          subtitle={t.subtitleTotal}
        />
        <ReporterStatCard
          icon={Clock}
          label={t.stats.pending}
          value={stats.pendingReview}
          color="amber"
          subtitle={t.subtitlePending}
        />
        <ReporterStatCard
          icon={CheckCircle}
          label={t.stats.published}
          value={stats.publishedArticles}
          color="blue"
          subtitle={t.subtitlePublished}
        />
        <ReporterStatCard
          icon={XCircle}
          label={t.stats.rejected}
          value={stats.rejectedArticles}
          color="red"
          subtitle={t.subtitleRejected}
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="mb-4 text-lg font-extrabold text-char-900">{t.quickActionsTitle}</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map(({ icon: Icon, labelEn, labelNe, path, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`flex items-center gap-3 rounded-3xl p-4 text-sm font-extrabold transition-all duration-200 hover:-translate-y-0.5 ${color}`}
            >
              <Icon size={20} />
              <span className="flex-1 text-left">{language === 'ne' ? labelNe : labelEn}</span>
              <ArrowRight size={16} className="shrink-0 opacity-60" />
            </button>
          ))}
        </div>
      </div>

      {/* Admin Remarks for rejected articles */}
      {rejectedArticles.length > 0 && (
        <div>
          <h3 className="mb-4 text-lg font-extrabold text-char-900">
            {t.adminRemarks}
          </h3>
          <div className="space-y-3">
            {rejectedArticles.map((article) => (
              <AdminRemarksCard
                key={article.id}
                article={article}
                language={language}
                onResubmit={() => navigate('/reporter/create-article', { state: { article } })}
                onDismiss={(id) => console.log('Dismiss', id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Articles & Assigned Events */}
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-extrabold text-char-900">{t.recentArticles}</h3>
            <button
              onClick={() => navigate('/reporter/articles')}
              className="flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider text-primary transition-colors hover:text-primary-300"
            >
              {t.viewAll}
              <ArrowRight size={14} />
            </button>
          </div>
          <ReporterArticleTable
            articles={recentArticles}
            language={language}
            onView={(a) => navigate('/reporter/create-article', { state: { article: a, viewOnly: true } })}
            onEdit={(a) => navigate('/reporter/create-article', { state: { article: a } })}
            onResubmit={(a) => navigate('/reporter/create-article', { state: { article: a } })}
          />
        </div>

        <div>
          <h3 className="mb-4 text-lg font-extrabold text-char-900">{t.assignedEvents}</h3>
          <div className="space-y-4">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event) => (
                <AssignedEventCard
                  key={event.id}
                  event={event}
                  language={language}
                  onSubmitReport={() => navigate('/reporter/events')}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center rounded-3xl border border-char-100 bg-white py-8">
                <p className="text-sm text-char-400">
                  {t.noEvents}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Media */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-extrabold text-char-900">{t.recentMedia}</h3>
          <button
            onClick={() => navigate('/reporter/media')}
            className="flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider text-primary transition-colors hover:text-primary-300"
          >
            {t.viewAll}
            <ArrowRight size={14} />
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {recentMedia.map((item) => (
            <MediaPreviewCard key={item.id} media={item} language={language} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReporterDashboard;
